import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getDataset } from '../datasets';
import { PreviewDataset } from '../preview.types';
import { getPreviewDatasetCode } from './previewMode';
import { routes } from './routes';

// Fail-closed mock backend for the "Try It Now" preview sandbox.
//
// dispatchMock() is only ever called when preview is ARMED (see previewAdapter). For a matched
// GET it resolves a synthetic AxiosResponse; for ANYTHING else — a non-GET (every write), or an
// unmatched path — it REJECTS and never falls through to the network. That single rule delivers
// two guarantees at once:
//   • fail-closed: the real API is never called in preview (ADR 0001 #2)
//   • read-only: every mutation is blocked (GET-only, and writes have no handler anyway)

export interface MockRouteContext {
  /** Parsed request URL — read `searchParams` from here. */
  url: URL;
  /** Path params captured from the route pattern (e.g. `:id`). */
  params: Record<string, string>;
  /** The active synthetic dataset (already resolved; never undefined). */
  dataset: PreviewDataset;
  config: InternalAxiosRequestConfig;
}

export interface MockRoute {
  /** Path pattern matched against the request, e.g. `/Teams/employees/:id`. GET-only by design. */
  pattern: string;
  /** Builds the response body (the `data` of the AxiosResponse). */
  build: (ctx: MockRouteContext) => unknown;
}

/**
 * Matches a route `pattern` against a request `pathname`, returning captured params or null.
 * Anchors on the `/api/` prefix (the API base path) so matching is exact per segment rather than
 * a loose suffix test — `:name` segments capture into params.
 */
export function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  const apiIdx = pathname.indexOf('/api/');
  const relative = apiIdx >= 0 ? pathname.slice(apiIdx + 4) : pathname;
  const patSegs = pattern.split('/').filter(Boolean);
  const pathSegs = relative.split('/').filter(Boolean);
  if (patSegs.length !== pathSegs.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patSegs.length; i++) {
    const pat = patSegs[i];
    if (pat.startsWith(':')) {
      params[pat.slice(1)] = decodeURIComponent(pathSegs[i]);
    } else if (pat !== pathSegs[i]) {
      return null;
    }
  }
  return params;
}

function currentDataset(): PreviewDataset {
  // getDataset falls back to the default, so this is always defined.
  return getDataset(getPreviewDatasetCode()) as PreviewDataset;
}

export async function dispatchMock(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  const method = (config.method ?? 'get').toLowerCase();
  const rawUrl = `${config.baseURL ?? ''}${config.url ?? ''}`;
  // Dummy base lets relative URLs parse; we only read pathname + searchParams.
  const url = new URL(rawUrl, 'http://preview.local');

  // Read-only: only GETs can ever match a route; everything else fails closed below.
  let params: Record<string, string> | null = null;
  const route =
    method === 'get'
      ? routes.find((r) => (params = matchPath(r.pattern, url.pathname)) !== null)
      : undefined;

  if (!route || params === null) {
    // FAIL CLOSED. Never proxy to the real API in preview.
    // A write (Create/Edit/Delete) is EXPECTED to be blocked — the preview is read-only. Reject with
    // a friendly, user-facing message + a distinct code so the UI can surface it gracefully (a gentle
    // "changes aren't saved" snackbar) rather than the raw developer text. An unmatched GET is a
    // genuine coverage gap, so keep its developer-facing message + console warning.
    if (method !== 'get') {
      return Promise.reject(
        new AxiosError(
          "You're exploring Klayo with sample data, so changes aren't saved in this preview.",
          'ERR_PREVIEW_READONLY',
          config,
          null
        )
      );
    }
    // The raw path is logged to the console for our diagnostics only (coverage gap to close), but the
    // user-facing message stays friendly — a prospect must never see "no mock handler for GET …".
    // eslint-disable-next-line no-console
    console.warn(`[preview mock] blocked unhandled GET ${url.pathname}`);
    return Promise.reject(
      new AxiosError(
        "This part of Klayo isn't available in the sample-data preview.",
        'ERR_PREVIEW_UNHANDLED',
        config,
        null
      )
    );
  }

  return {
    data: route.build({ url, params, dataset: currentDataset(), config }),
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
  } as AxiosResponse;
}
